import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError, UNAUTHORIZED_EVENT, type User } from "./api";

interface AuthState {
  user: User | null;
  loading: boolean;
  login(username: string, password: string, code?: string): Promise<void>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

// Who is signed in can change behind a page's back: signing out, signing in as someone
// else in another tab, or the session expiring. Each of these must leave no page of the
// previous user on screen: a patient who signs in after an admin starts at the home page,
// never on the admin's last page. Signing back in as the same user (after an expired
// session) stays on the page they were on.
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const navRef = useRef(navigate);      // navigate changes with the location; keep callbacks stable
  navRef.current = navigate;
  // the last user this tab showed, kept across reloads of the tab
  const lastUserId = useRef<number | null>(readLastUser());

  const show = useCallback((u: User | null) => {
    if (u && lastUserId.current !== null && u.id !== lastUserId.current) navRef.current("/", { replace: true });
    if (u) {
      lastUserId.current = u.id;
      try { sessionStorage.setItem(LAST_USER_KEY, String(u.id)); } catch { /* storage blocked */ }
    }
    // unchanged: keep the same object, so pages do not re-run their effects on every recheck
    setUser((cur) => (cur && u && JSON.stringify(cur) === JSON.stringify(u) ? cur : u));
  }, []);

  const refresh = useCallback(async () => {
    try {
      show(await api.me());
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setUser(null);
      else throw e;
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Another tab signed out or signed in as someone else: notice on return to this tab.
  useEffect(() => {
    const recheck = () => {
      if (document.visibilityState === "visible") void refresh().catch(() => undefined);
    };
    const expired = () => setUser(null);
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    window.addEventListener(UNAUTHORIZED_EVENT, expired);
    return () => {
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("focus", recheck);
      window.removeEventListener(UNAUTHORIZED_EVENT, expired);
    };
  }, [refresh]);

  const login = useCallback(async (u: string, p: string, code?: string) => {
    show(await api.login(u, p, code));
  }, [show]);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* already signed out on the server: the cookie is cleared all the same */
    } finally {
      setUser(null);
      navRef.current("/", { replace: true });
    }
  }, []);

  return <Ctx.Provider value={{ user, loading, login, logout, refresh }}>{children}</Ctx.Provider>;
}

const LAST_USER_KEY = "vtlite:last-user";

function readLastUser(): number | null {
  try {
    const v = sessionStorage.getItem(LAST_USER_KEY);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
